import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
  "#06b6d4", "#f97316", "#14b8a6", "#a855f7", "#e11d48", "#84cc16",
];

const FrequencyChart = ({ frequencies, options, conditions, height = 200 }) => {
  if (!frequencies?.byCondition || Object.keys(frequencies.byCondition).length === 0) {
    return <p className="text-gray-500 text-sm">No data</p>;
  }

  const conditionIds = conditions.map((c) => c.conditionId);
  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  // Build option labels map
  const optionLabels = {};
  (options || []).forEach((o) => {
    optionLabels[o.value] = o.label || o.value;
  });

  // Get all unique values
  const allValues = new Set();
  Object.values(frequencies.byCondition).forEach((condFreqs) => {
    Object.keys(condFreqs).forEach((v) => allValues.add(v));
  });
  Object.keys(frequencies.overall || {}).forEach((v) => allValues.add(v));

  // Preserve option order if available
  const orderedValues = options
    ? options.map((o) => o.value).filter((v) => allValues.has(v))
    : [...allValues];

  const chartData = orderedValues.map((val) => {
    const row = { name: optionLabels[val] || val };
    conditionIds.forEach((cid) => {
      row[cid] = frequencies.byCondition[cid]?.[val] || 0;
    });
    return row;
  });

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis type="number" tick={{ fill: "#9ca3af", fontSize: 11 }} stroke="#4b5563" />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ fill: "#9ca3af", fontSize: 11 }}
          width={120}
          stroke="#4b5563"
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1f2937",
            border: "1px solid #4b5563",
            borderRadius: "6px",
          }}
          labelStyle={{ color: "#fff", fontSize: 12 }}
          itemStyle={{ fontSize: 11 }}
        />
        <Legend
          formatter={(value) => (
            <span className="text-gray-300 text-xs">{conditionLabels[value] || value}</span>
          )}
        />
        {conditionIds.map((cid, i) => (
          <Bar
            key={cid}
            dataKey={cid}
            fill={CONDITION_COLORS[i % CONDITION_COLORS.length]}
            name={cid}
            stackId="stack"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default FrequencyChart;
